/**
 * Chaupar Game — Main Container Component
 *
 * Orchestrates: mode selection → engine init → dice roll → move selection →
 * move execution → animation → turn management → win.
 *
 * Wrapped in ErrorBoundary. Uses useAuthentication().
 * Responsive layout: board + controls.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { useAuthentication } from '../../hooks/useAuthentication';
import { useNotifications } from '../../hooks/useNotifications';
import { NotificationType } from '../../services/NotificationService';
import { GameSessionManager } from '../../services/GameSessionManager';
import { GameCoach } from '../../components/GameCoach';
import { GameCoachService, GameContext } from '../../services/GameCoachService';
import { chauparKnowledge } from '../../services/gameCoachKnowledge';
import { HelpPanel, HelpSection } from '../../components/HelpPanel';
import { ChauparEngine } from '../ChauparEngine';
import { ChauparAI } from '../ChauparAI';
import { ChauparBoard } from './ChauparBoard';
import { ChauparDice } from './ChauparDice';
import { ChauparGameModeSelector } from './ChauparGameModeSelector';
import { UniversalGameLayout, UniversalGameLayoutConfig } from '../../components/UniversalGameLayout';
import { ChauparErrorHandler } from '../services/ChauparErrorHandler';
import { ChauparStatisticsService } from '../services/ChauparStatisticsService';
import { ChauparGamePersistence } from '../services/ChauparGamePersistence';
import {
  ChauparGameSettings,
  ChauparGameState,
  ChauparMove,
  DiceResult,
  PlayerColor,
  PlayerType,
  PawnAnimation,
  ChauparGameProps,
  PLAYER_COLORS,
  TEAMS,
} from '../types';
import './ChauparGame.css';

type GameView = 'mode-select' | 'playing' | 'game-over';
type GamePhase = 'waiting' | 'rolling' | 'moving' | 'animating' | 'completed';

const ChauparGameInner: React.FC<ChauparGameProps> = ({
  onExit,
  initialRoomCode,
}) => {
  // State
  const [gameView, setGameView] = useState<GameView>('mode-select');
  const [gamePhase, setGamePhase] = useState<GamePhase>('waiting');
  const [gameState, setGameState] = useState<ChauparGameState | null>(null);
  const [validMoves, setValidMoves] = useState<ChauparMove[]>([]);
  const [selectedPawn, setSelectedPawn] = useState<{ player: PlayerColor; pawnIndex: number } | null>(null);
  const [selectedDie, setSelectedDie] = useState<number | null>(null);
  const [isRolling, setIsRolling] = useState(false);
  const [animatingMove, setAnimatingMove] = useState<PawnAnimation | null>(null);
  const [settings, setSettings] = useState<ChauparGameSettings | null>(null);
  const [aiSpeed, setAiSpeed] = useState(1000); // ms delay for AI moves
  const [gameSessionId, setGameSessionId] = useState<string | null>(null);
  const [showHelp, setShowHelp] = useState(false);

  // Refs
  const engineRef = useRef<ChauparEngine>(new ChauparEngine());
  const aiRef = useRef<ChauparAI | null>(null);
  const aiTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const coachRef = useRef(new GameCoachService('Chaupar', chauparKnowledge));

  // Hooks
  const { isAuthenticated, guestGameCount } = useAuthentication();
  const { addNotification } = useNotifications();

  // Services
  const gameSessionManager = GameSessionManager.getInstance();
  const statisticsService = useRef(new ChauparStatisticsService());
  const persistence = useRef(new ChauparGamePersistence());

  // Cleanup AI timeout on unmount
  useEffect(() => {
    return () => {
      if (aiTimeoutRef.current) {
        clearTimeout(aiTimeoutRef.current);
      }
    };
  }, []);

  // Start game
  const handleStartGame = useCallback((gameSettings: ChauparGameSettings) => {
    setSettings(gameSettings);
    const engine = engineRef.current;

    let playerTypes: PlayerType[];
    if (gameSettings.mode === 'singleplayer') {
      // Human is Red, AI teammate is Green, opponents are Blue and Yellow
      playerTypes = ['human', 'ai', 'ai', 'ai'];
      aiRef.current = new ChauparAI(gameSettings.aiDifficulty || 'medium', engine);
    } else if (gameSettings.mode === 'local-multiplayer') {
      playerTypes = ['human', 'human', 'human', 'human'];
    } else {
      playerTypes = ['human', 'human', 'human', 'human'];
    }

    engine.initializeGame(playerTypes, gameSettings);
    const state = engine.getGameState();
    setGameState(state);
    setGameView('playing');
    setGamePhase('waiting');

    // Start game session tracking
    const sessionId = gameSessionManager.startGameSession('chaupar');
    setGameSessionId(sessionId);

    addNotification({
      type: NotificationType.SUCCESS,
      title: 'Game Started',
      message: 'Chaupar game has begun! Red rolls first.',
      duration: 3000,
    });
  }, [addNotification, gameSessionManager]);

  // Roll dice
  const handleRollDice = useCallback(() => {
    if (!gameState || gamePhase !== 'waiting') return;

    const engine = engineRef.current;
    setIsRolling(true);
    setGamePhase('rolling');

    // Animate for 1 second then show result
    setTimeout(() => {
      const diceResult = engine.rollDice();
      const newState = engine.getGameState();
      setGameState(newState);
      setIsRolling(false);
      setGamePhase('moving');

      // Get valid moves
      const moves = engine.getValidMoves(newState.currentTurn);
      setValidMoves(moves);

      // Check for triple
      if (diceResult.isTriple) {
        addNotification({
          type: NotificationType.INFO,
          title: 'Triple!',
          message: `${diceResult.bonusValue > 0 ? `Bonus +${diceResult.bonusValue}! ` : ''}Extra turn granted!`,
          duration: 3000,
        });
      }

      // If no valid moves (only skips), auto-pass
      const realMoves = moves.filter(m => m.action !== 'skip');
      if (realMoves.length === 0) {
        addNotification({
          type: NotificationType.INFO,
          title: 'No Moves',
          message: `${newState.currentTurn} has no valid moves. Turn passed.`,
          duration: 2000,
        });
        setTimeout(() => {
          endTurn();
        }, 1500);
      }
    }, 1000);
  }, [gameState, gamePhase, addNotification]);

  // Handle die selection
  const handleDieSelect = useCallback((dieIndex: number) => {
    if (gamePhase !== 'moving') return;
    setSelectedDie(dieIndex);
    setSelectedPawn(null);
  }, [gamePhase]);

  // Handle pawn selection
  const handlePawnSelect = useCallback((player: PlayerColor, pawnIndex: number) => {
    if (gamePhase !== 'moving' || !gameState) return;

    const currentPlayer = gameState.currentTurn;
    if (player !== currentPlayer) return;

    // If a die is selected, try to execute the move
    if (selectedDie !== null) {
      const move = validMoves.find(
        m => m.player === player && m.pawnIndex === pawnIndex && m.dieIndex === selectedDie && m.action !== 'skip'
      );

      if (move) {
        executePlayerMove(move);
        return;
      }
    }

    // Otherwise just select the pawn and auto-select first available die
    setSelectedPawn({ player, pawnIndex });

    // Find first unused die that works with this pawn
    const availableMoves = validMoves.filter(
      m => m.player === player && m.pawnIndex === pawnIndex && m.action !== 'skip'
    );
    if (availableMoves.length > 0 && selectedDie === null) {
      setSelectedDie(availableMoves[0].dieIndex);
    }
  }, [gamePhase, gameState, selectedDie, validMoves]);

  // Execute a player move
  const executePlayerMove = useCallback((move: ChauparMove) => {
    const engine = engineRef.current;
    const result = engine.executeMove(move.player, move);

    if (result.success) {
      const newState = engine.getGameState();
      setGameState(newState);

      // Update valid moves
      const moves = engine.getValidMoves(newState.currentTurn);
      setValidMoves(moves);
      setSelectedDie(null);
      setSelectedPawn(null);

      // Notify on capture
      if (result.captured) {
        addNotification({
          type: NotificationType.WARNING,
          title: 'Capture!',
          message: `${move.player} eliminated ${result.captured.player}'s pawn!`,
          duration: 3000,
        });
      }

      // Notify on reaching home
      if (result.reachedHome) {
        addNotification({
          type: NotificationType.SUCCESS,
          title: 'Home!',
          message: `${move.player}'s pawn reached the Charkoni!`,
          duration: 3000,
        });
      }

      // Check if all dice are used
      if (newState.usedDice.every(Boolean)) {
        endTurn();
      }

      // Check game over
      if (engine.isGameOver()) {
        handleGameOver(newState);
      }
    }
  }, [addNotification]);

  // End turn
  const endTurn = useCallback(() => {
    const engine = engineRef.current;
    const nextPlayer = engine.advanceTurn();
    const newState = engine.getGameState();
    setGameState(newState);
    setValidMoves([]);
    setSelectedDie(null);
    setSelectedPawn(null);
    setGamePhase('waiting');

    // Save game state
    persistence.current.saveGame(newState);

    // Schedule AI move if next player is AI
    if (newState.playerTypes[nextPlayer] === 'ai' && newState.gameStatus.type === 'active') {
      scheduleAITurn(nextPlayer);
    }
  }, []);

  // Schedule AI turn
  const scheduleAITurn = useCallback((player: PlayerColor) => {
    aiTimeoutRef.current = setTimeout(() => {
      handleAITurn(player);
    }, aiSpeed);
  }, [aiSpeed]);

  // Handle AI turn
  const handleAITurn = useCallback((player: PlayerColor) => {
    const engine = engineRef.current;
    const ai = aiRef.current;
    if (!ai) return;

    // Roll dice
    const diceResult = engine.rollDice();
    const stateAfterRoll = engine.getGameState();
    setGameState(stateAfterRoll);

    // Get AI moves
    setTimeout(() => {
      const moves = ai.selectMoves(player, diceResult);

      // Execute moves (AI already executed them via engine in selectMoves)
      const newState = engine.getGameState();
      setGameState(newState);

      // Check game over
      if (engine.isGameOver()) {
        handleGameOver(newState);
        return;
      }

      // Advance turn
      const nextPlayer = engine.advanceTurn();
      const finalState = engine.getGameState();
      setGameState(finalState);
      setGamePhase('waiting');

      // If next player is also AI, schedule their turn
      if (finalState.playerTypes[nextPlayer] === 'ai' && finalState.gameStatus.type === 'active') {
        scheduleAITurn(nextPlayer);
      }
    }, 500);
  }, [scheduleAITurn]);

  // Handle game over
  const handleGameOver = useCallback((state: ChauparGameState) => {
    setGamePhase('completed');
    setGameView('game-over');

    if (gameSessionId) {
      gameSessionManager.endGameSession(gameSessionId, true);
    }

    const winningTeam = state.gameStatus.type === 'win' ? state.gameStatus.winningTeam : null;
    const teamName = winningTeam === 'teamA' ? 'Team A (Red + Green)' : 'Team B (Blue + Yellow)';

    addNotification({
      type: NotificationType.SUCCESS,
      title: 'Game Over!',
      message: `${teamName} wins!`,
      duration: 5000,
    });

    // Record statistics
    statisticsService.current.recordResult({
      gameId: state.gameId,
      mode: settings?.mode || 'singleplayer',
      difficulty: settings?.aiDifficulty,
      winningTeam: winningTeam || undefined,
      reason: 'all_pawns_home',
      totalMoves: state.moveHistory.length,
      capturesMade: state.moveHistory.filter(m => m.captured).length,
      specialCombinations: 0,
      duration: Date.now() - state.startedAt,
      completedAt: Date.now(),
    });
  }, [gameSessionId, gameSessionManager, addNotification, settings]);

  // Play again
  const handlePlayAgain = useCallback(() => {
    if (settings) {
      handleStartGame(settings);
    }
  }, [settings, handleStartGame]);

  // Exit game
  const handleExit = useCallback(() => {
    if (aiTimeoutRef.current) {
      clearTimeout(aiTimeoutRef.current);
    }
    if (gameSessionId) {
      gameSessionManager.endGameSession(gameSessionId, false);
    }
    if (onExit) {
      onExit();
    }
  }, [gameSessionId, gameSessionManager, onExit]);

  // Determine if it's the human player's turn
  const isPlayerTurn = gameState
    ? gameState.playerTypes[gameState.currentTurn] === 'human' && gamePhase === 'moving'
    : false;

  const canRoll = gameState
    ? gameState.playerTypes[gameState.currentTurn] === 'human' && gamePhase === 'waiting' && gameState.gameStatus.type === 'active'
    : false;

  // Build game context for AI Coach
  const coachContext: GameContext | null = gameState ? {
    gameName: 'Chaupar',
    gameState: `Turn ${gameState.moveHistory.length + 1}. Current player: ${gameState.currentTurn}. Team A (Red+Green) pawns home: ${gameState.pawnsHome.red + gameState.pawnsHome.green}/8. Team B (Blue+Yellow) pawns home: ${gameState.pawnsHome.blue + gameState.pawnsHome.yellow}/8.`,
    playerContext: `You are playing as Red (Team A with Green). ${gameState.lastDiceResult ? `Dice: [${gameState.lastDiceResult.values.join(', ')}]` : 'Waiting to roll.'} ${validMoves.filter(m => m.action !== 'skip').length} valid moves available.`,
    validMoves: validMoves.filter(m => m.action !== 'skip').map(m => `Pawn ${m.pawnIndex + 1}: ${m.action} (die ${m.dieValue})`).join('; '),
    lastMove: gameState.moveHistory.length > 0 ? `Last: ${gameState.moveHistory[gameState.moveHistory.length - 1].player} moved pawn ${gameState.moveHistory[gameState.moveHistory.length - 1].pawnIndex + 1}` : undefined,
  } : null;

  // Help panel content
  const helpSections: HelpSection[] = [
    {
      title: 'Rules',
      icon: '📜',
      content: [
        'Chaupar is a royal Indian cross-and-circle game for 4 players in 2 teams.',
        'Team A: Red + Green. Team B: Blue + Yellow.',
        'Each player has 4 pawns starting on the track at historically authentic positions.',
        'Roll 3 dice per turn and assign each die to a different pawn.',
        'Move pawns clockwise around the 68-square track.',
        'First team to get all 8 pawns home wins!',
      ],
    },
    {
      title: 'Dice',
      icon: '🎲',
      content: [
        'Roll 3 dice each turn — assign each to a different pawn.',
        'Dice use authentic values: 1, 2, 5, or 6.',
        'Triple 1s: bonus move of 10 + extra turn.',
        'Triple 6s: bonus move of 18 + extra turn.',
        'Any other triple: extra turn granted.',
      ],
    },
    {
      title: 'Captures',
      icon: '⚔️',
      content: [
        'Land on an opponent\'s pawn to capture (eliminate) it.',
        'Captured pawns are PERMANENTLY removed — they don\'t return!',
        'You cannot capture pawns that are part of a block.',
        'Captures are the most powerful move in Chaupar.',
      ],
    },
    {
      title: 'Blocks',
      icon: '🛡️',
      content: [
        'Two same-team pawns on one square form a block.',
        'Blocks cannot be passed through or landed on by opponents.',
        'Your partner\'s pawn counts for forming blocks!',
        'Use blocks to protect key positions and trap opponents.',
      ],
    },
    {
      title: 'Controls',
      icon: '🎮',
      content: [
        'Click "Roll Dice" to roll your 3 dice.',
        'Click a die to select it, then click a pawn to move.',
        'Or click a pawn first — the first valid die auto-selects.',
        'Highlighted pawns have valid moves available.',
        'The AI Coach (singleplayer) provides strategy tips.',
      ],
    },
  ];

  // =========================================================================
  // Render
  // =========================================================================

  // Mode Selection
  if (gameView === 'mode-select') {
    return (
      <div className="chaupar-game">
        {/* Help Panel */}
        <HelpPanel
          gameName="Chaupar"
          sections={helpSections}
          isOpen={showHelp}
          onClose={() => setShowHelp(false)}
          accentColor="#c9a84c"
        />

        <ChauparGameModeSelector
          onStartGame={handleStartGame}
          guestGamesRemaining={Math.max(0, 3 - guestGameCount)}
          isAuthenticated={isAuthenticated}
          isOnline={true}
        />
      </div>
    );
  }

  // No game state yet
  if (!gameState) return null;

  // Game over config
  const winningTeam = gameState.gameStatus.type === 'win' ? gameState.gameStatus.winningTeam : null;
  const teamName = winningTeam === 'teamA' ? 'Team A (Red + Green)' : 'Team B (Blue + Yellow)';

  const gameOverConfig = (gameView === 'game-over' && gameState.gameStatus.type === 'win') ? {
    show: true,
    result: 'win' as 'win' | 'loss' | 'draw',
    title: '🏆 Game Over!',
    subtitle: `${teamName} wins! (${gameState.moveHistory.length} moves, ${gameState.moveHistory.filter(m => m.captured).length} captures)`,
    icon: '🏆',
    onPlayAgain: handlePlayAgain,
    onExit: handleExit,
  } : undefined;

  const layoutConfig: UniversalGameLayoutConfig = {
    gameName: 'Chaupar',
    board: (
      <ChauparBoard
        gameState={gameState}
        currentPlayer={gameState.currentTurn}
        validMoves={validMoves}
        diceResult={gameState.lastDiceResult}
        onPawnSelect={handlePawnSelect}
        isPlayerTurn={isPlayerTurn}
        selectedPawn={selectedPawn}
        animatingMove={animatingMove}
      />
    ),
    leftPanels: [
      {
        id: 'teams',
        title: 'Teams',
        icon: '👥',
        content: (
          <div className="chaupar-game__team-info" role="status" aria-live="polite">
            <div className="chaupar-game__team">
              <strong>Team A (Red + Green)</strong>
              <span>🏠 {gameState.pawnsHome.red + gameState.pawnsHome.green}/8</span>
            </div>
            <div className="chaupar-game__team">
              <strong>Team B (Blue + Yellow)</strong>
              <span>🏠 {gameState.pawnsHome.blue + gameState.pawnsHome.yellow}/8</span>
            </div>
          </div>
        ),
      },
      {
        id: 'dice',
        title: 'Dice',
        icon: '🎲',
        content: (
          <ChauparDice
            values={gameState.lastDiceResult?.values || null}
            isRolling={isRolling}
            onRoll={handleRollDice}
            canRoll={canRoll}
            usedDice={gameState.usedDice}
            onDieSelect={handleDieSelect}
            selectedDie={selectedDie}
            isTriple={gameState.lastDiceResult?.isTriple}
            bonusValue={gameState.lastDiceResult?.bonusValue}
          />
        ),
      },
    ],
    rightPanels: settings?.mode === 'singleplayer' ? [
      {
        id: 'coach',
        title: 'AI Coach',
        icon: '🧠',
        content: (
          <GameCoach
            coach={coachRef.current}
            gameContext={coachContext}
            isPlayerTurn={isPlayerTurn}
            quickQuestions={['Should I capture or block?', 'How to use these dice?', 'Team strategy?', 'Starting positions?']}
            accentColor="#c9a84c"
          />
        ),
      },
    ] : undefined,
    statusBar: {
      turnIndicator: (
        <span>
          {gameState.playerTypes[gameState.currentTurn] === 'ai'
            ? `AI (${gameState.currentTurn}) is thinking...`
            : `${gameState.currentTurn}'s turn`}
          {gamePhase === 'moving' && isPlayerTurn && (
            <span> — Select a pawn to move</span>
          )}
        </span>
      ),
      gameStatus: gameState.gameStatus.type === 'win' ? 'Game Over' : undefined,
      gameStatusType: gameState.gameStatus.type === 'win' ? 'ended' : 'active',
      aiThinking: gameState.playerTypes[gameState.currentTurn] === 'ai',
    },
    actions: [
      { id: 'new-game', label: 'New Game', icon: '🔄', onClick: handlePlayAgain, variant: 'primary' },
      { id: 'help', label: 'Help', icon: '❓', onClick: () => setShowHelp(true), variant: 'secondary' },
      { id: 'exit', label: 'Exit', icon: '🚪', onClick: handleExit, variant: 'secondary' },
    ],
    gameOver: gameOverConfig,
  };

  return (
    <div className="chaupar-game">
      {/* Help Panel */}
      <HelpPanel
        gameName="Chaupar"
        sections={helpSections}
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        accentColor="#c9a84c"
      />

      <UniversalGameLayout config={layoutConfig} />
    </div>
  );
};

// Wrap in ErrorBoundary
export const ChauparGame: React.FC<ChauparGameProps> = (props) => (
  <ErrorBoundary componentName="Chaupar">
    <ChauparGameInner {...props} />
  </ErrorBoundary>
);

export default ChauparGame;
